import {AlertSeverity} from "@/medication-checkup/interfaces/alerts";

/**
 * How a level of the scale of Vidal reads in the checklist of the step: an
 * alert of its own, a point to keep an eye on, or information.
 */
export type AlertLevelReading = 'alert' | 'warning' | 'information';

/**
 * One level of the scale of Vidal, as the scenes read and paint it: the dot
 * and the top border of a card, its count chip, the colour its own words
 * take where they are read in a row, and what it amounts to at a glance.
 *
 * The whole scale lives in this one table: a level painted in red here and
 * counted as information there would tell the pharmacist two things at once.
 */
export interface AlertLevelTone {
    dot: string;
    border: string;
    chip: string;
    text: string;
    reading: AlertLevelReading;
}

const TONES: Record<string, AlertLevelTone> = {
    LEVEL_4: {
        dot: 'bg-red-light',
        border: 'border-t-red-light',
        chip: 'bg-red-light/15 text-red-light',
        text: 'text-red-light',
        reading: 'alert'
    },
    LEVEL_3: {
        dot: 'bg-orange',
        border: 'border-t-orange',
        chip: 'bg-orange/30 text-gray-dark',
        text: 'text-gray-dark',
        reading: 'alert'
    },
    LEVEL_2: {
        dot: 'bg-lightPurple-darkest',
        border: 'border-t-lightPurple-darkest',
        chip: 'bg-lightPurple/35 text-lightPurple-darkest',
        text: 'text-lightPurple-darkest',
        reading: 'warning'
    },
    LEVEL_1: {
        dot: 'bg-sky-400',
        border: 'border-t-sky-400',
        chip: 'bg-sky-100 text-sky-700',
        text: 'text-sky-700',
        reading: 'warning'
    },
    INFO: {
        dot: 'bg-gray',
        border: 'border-t-gray',
        chip: 'bg-gray-light text-muted-foreground',
        text: 'text-muted-foreground',
        reading: 'information'
    }
};

/**
 * The tone of a level — the one of an information for a code off the scale.
 */
export const alertLevelTone = (severity: AlertSeverity): AlertLevelTone => {
    return TONES[severity] ?? TONES.INFO;
};
